(ns ycweb.app.website.cart.addresses
  (:require
   [ajax.core :as ajax]
   [rum.core :as rum]
   [ycweb.components.common :as cmn]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]))

(defn address-list-atom [app-atom]
  (rum/cursor-in app-atom [:addresses :list]))

(defn default-country-uuid
  "Get the default country UUID (South Africa) from app state.
   This is populated by the cart-addresses API call."
  [app-atom]
  (cmn/curact app-atom [:addresses :default_country_uuid]))

(defn countries
  "Get the countries map from app state.
   This is populated by the cart-addresses API call.
   Returns a map indexed by country UUID with keys: :uuid, :name, :country_code"
  [app-atom]
  (->>
    (cmn/curact app-atom [:addresses :countries])
    (vals)
    (sort-by :name)))

(defn find-country
  "Resolve a country entry from [:addresses :countries] by uuid. Compares as strings
   so it works whether the uuid is a cljs UUID (EDN reads / the [:addresses :countries]
   keys) or a plain string (a <select> DOM value writes a string)."
  [app-atom country-uuid]
  (when (and country-uuid (not= 0 country-uuid))
    (let [target (str country-uuid)]
      (->> (cmn/curact app-atom [:addresses :countries])
        (vals)
        (some (fn [c] (when (= target (str (:uuid c))) c)))))))

(defn south-african?
  "True when the address's country is South Africa. A nil/unresolved country_uuid
   (e.g. an address saved without a country) is treated as not South African."
  [app-atom address]
  (let [sa-code (cmn/curact app-atom [:config :sa-country-code])
        country (find-country app-atom (:country_uuid address))]
    (boolean (and country (= (:country_code country) sa-code)))))

(defonce complete-fn-atom (atom {}))

(defn fetch-saved-addresses
  "Fetch user's saved addresses from the cart API and store in app state.
   Loads addresses list in cart-compatible format.
   Only executes for authenticated users - guest users are skipped."
  [app-atom & [complete-fn]]

  (swap! complete-fn-atom assoc :web.cart/addresses complete-fn)

  (comms/ajax-call :get :web.cart/addresses {} app-atom))

(defmethod comms/receive :web.cart/addresses [app-atom response]
  (reset! (address-list-atom app-atom) (:addresses response))
  ;; Store countries and default country UUID for address forms
  (swap! app-atom assoc-in [:addresses :countries]
    (->>
      (:countries response)
      (vals)
      (u/index-by :uuid)))
  (swap! app-atom assoc-in [:addresses :default_country_uuid] (:default_country_uuid response))
  (when-let [complete-fn (get @complete-fn-atom :web.cart/addresses)]
    (complete-fn response)))

(defn cart-address->myaccount-address
  "Convert cart address format to myaccount API format.
   Maps field names and structure from cart delivery/billing shapes to address-save-input shape.

   Parameters:
   - cart-address: Address data in cart format (from delivery or billing shape)
   - options: Optional map with keys:
     - :existing-uuid - UUID of existing address to update
     - :set-billing? - Whether to set as default billing address
     - :set-delivery? - Whether to set as default delivery address
     - :country-uuid - Country UUID (defaults to South Africa if available from app state)"
  [cart-address]
  {:address
   {"ent/uuid" (str (or (:uuid cart-address) (u/uuid)))
    ;; Stringify: country_uuid may be a cljs UUID object (the picker uses :type :uuid),
    ;; which won't JSON-serialize to a bare uuid for this JSON POST.
    "address/country" {"ent/uuid" (str (:country_uuid cart-address))}
    "address/firstname" (:firstname cart-address)
    "address/lastname" (:lastname cart-address)
    "address/company" (:company cart-address)
    "address/vatnumber" (:vatnumber cart-address)
    "address/address" (:address cart-address)
    "address/suburb" (:suburb cart-address)
    "address/city" (:city cart-address)
    "address/postalcode" (:postalcode cart-address)
    "address/type" (:addresstype cart-address)
    "address/cell" (:cell cart-address)
    "address/tel" (:cell cart-address)
    "address/setdelivery" (:default-delivery cart-address)
    "address/setbilling" (:default-billing cart-address)}})

(defn select-saved-address
  "Select a saved address and populate cart form fields.
   Uses the standardized cart-addresses-shape format throughout.

   Parameters:
   - app-atom: Main application state atom
   - address-data: Address object from saved addresses API (cart-addresses-shape)
   - type: Either 'delivery' or 'billing'
   - complete-fn: Optional callback function

   Side effects:
   - Updates cart state with address fields
   - Sets addressuuid for backend reference
   - Stores current new address details if switching from new address mode"
  [app-atom address-atom address-data address-type]
  (let [current-address-data @address-atom]

    (cond
      (:uuid current-address-data)
      (reset! (rum/cursor-in app-atom [:addresses address-type :selected]) address-data)

      :else
      (swap! app-atom assoc-in [:addresses address-type :new-address] current-address-data))

    ;; Update cart state with address fields only, preserving billing-specific keys like :email
    (swap! address-atom merge
      (select-keys address-data
        [:uuid :address :suburb :city :postalcode :addresstype
         :googleaddress :longitude :latitude
         :firstname :lastname :company :vatnumber :tel :cell
         :country_uuid :display_name]))))

(defn select-new-address
  "Prepare for new address entry: restore previously entered new-address
   details, or clear the current selection's identity."
  [app-atom address-atom address-type]
  (let [stored-new-address (get-in @app-atom [:addresses address-type :new-address])]
    (if stored-new-address
      (reset! address-atom (dissoc stored-new-address :uuid))
      ;; Shed a selected saved address's identity, else saving would update that row
      (swap! address-atom dissoc :uuid :display_name))))

;; ============================================================================
;; Helper Functions for New Address Storage
;; ============================================================================

(defn store-current-new-address
  "Store the current new address details for later restoration.
   Call this before switching away from new address mode."
  [app-atom address-type]
  (let [cart-atom (rum/cursor-in app-atom [:cart-summary])
        current-address-data (get-in @cart-atom [:cart address-type])]

    (swap! app-atom assoc-in [:addresses :new-address address-type] current-address-data)))

(defn clear-stored-new-address
  "Clear stored new address details for the specified type."
  [app-atom address-type]
  (swap! app-atom update-in [:addresses :new-address] dissoc address-type)
  (println "Cleared stored new address details for" address-type))

(defn has-stored-new-address?
  "Check if there are stored new address details for the specified type."
  [app-atom address-type]
  (boolean (seq (get-in @app-atom [:addresses :new-address address-type]))))

(defn get-stored-new-address
  "Get stored new address details for the specified type."
  [app-atom address-type]
  (get-in @app-atom [:addresses :new-address address-type]))

;; ============================================================================
;; Guest Address Management
;; ============================================================================

(defn is-user-logged-in?
  "Check if the user is logged in based on cart summary data."
  [cart-summary-atom]
  (let [cart-summary @cart-summary-atom
        user (get cart-summary :user)]
    (not (:guest user))))

;; ============================================================================
;; Updated Save Address Function
;; ============================================================================

(defn save-address
  "Save address changes to the user's address book.
   For logged-in users: saves to backend via myaccount API
   For guest users: saves locally in app state

   Parameters:
   - app-atom: Main application state atom
   - address-atom: Address data atom
   - selected-atom: Selected address atom
   - complete-fn: Callback with (success? new-address-uuid)

   Returns: Initiates async operation, result via callback

   Side effects:
   - For logged-in users: Calls myaccount/addresssave API
   - For guest users: Stores in local state
   - Updates local saved addresses cache
   - Auto-selects the saved address
   - Handles error states with toaster notifications"
  [app-atom address-atom selected-atom & [complete-fn]]
  (let [cart-summary-atom (rum/cursor-in app-atom [:cart-summary])
        logged-in? (is-user-logged-in? cart-summary-atom)]

    (cond
      ;; Guest user: set locally
      (not logged-in?)
      (let [address
            (update @address-atom :uuid #(or % (str (u/uuid))))]

        (swap! (address-list-atom app-atom)
          (fn [addresses]
            (cond
              (contains? (set (map :uuid addresses)) (:uuid address))
              (map
                (fn [a]
                  (if (= (:uuid a) (:uuid address))
                    address
                    a))
                addresses)

              :else
              (conj addresses address))))

        (reset! selected-atom address)
        (swap! address-atom merge
          (select-keys address
            [:uuid :address :suburb :city :postalcode :addresstype
             :googleaddress :longitude :latitude
             :firstname :lastname :company :vatnumber :tel :cell
             :country_uuid :display_name]))
        (toaster/open-toast app-atom :success "Address saved" {:closetime 2000 :show-close-button true})
        (when complete-fn (complete-fn true (:uuid address))))

      :else
      #?(:clj nil
         :cljs
         (let [;; Convert cart format to myaccount format. Persist the address's real
               ;; country (billing may be non-SA); fall back to SA only when none is set.
               address @address-atom
               myaccount-address-data
               (cart-address->myaccount-address
                 (if (:country_uuid address)
                   address
                   (assoc address :country_uuid (default-country-uuid app-atom))))

               is-new-address? (nil? (:uuid address))]

           (ajax/POST "/api/myaccount/addresssave"
             {:params myaccount-address-data
              :format :json
              :response-format :json
              :headers {"Accept" "application/json"}
              :keywords? true
              :error-handler
              (fn [response]
                (println "Error saving address:" (pr-str response))
                (toaster/open-toast app-atom :error
                  "Failed to save address. Please try again."
                  {:closetime 5000 :show-close-button true})
                (when complete-fn (complete-fn false nil)))
              :handler
              (fn [response]
                (println "Address save response:" (pr-str response))
                (if (:success response)
                  (do
                    (reset! selected-atom (select-keys address (keys @selected-atom)))
                    ;; Refresh saved addresses list to include the new/updated address
                    (fetch-saved-addresses app-atom
                      (fn [fetch-success?]
                        (if fetch-success?
                          (do
                            (toaster/open-toast app-atom :success
                              (if is-new-address? "Address saved" "Address updated")
                              {:closetime 2000 :show-close-button true})
                            (when complete-fn
                              (complete-fn true (or (:uuid response) (str (:uuid address))))))
                          (when complete-fn (complete-fn false nil))))))
                  (do
                    (toaster/open-toast app-atom :error
                      "Failed to save address. Please try again."
                      {:closetime 5000 :show-close-button true})
                    (when complete-fn (complete-fn false nil)))))}))))))


